// @ts-nocheck
/*
 * SAGA NAME CONVENTION: camelCase
 * <NOUN><VERB>
 * RequestStart
*/
import _ from "lodash";

import { v4 as uuidv4 } from "uuid";
import delay from "@redux-saga/delay-p";
import { put, call, select, fork, takeEvery } from "redux-saga/effects";
import FullStory from "services/Fullstory";
import Dengage from "services/Dengage";
import historyInstance from "utils/history";
import { matchRouteItem } from "utils/routeMatcher";
import type { MemberDetailsStateType, RouteObjectType } from "@src/api-new/generated-api.schemas";
import {
  getApiCustomerGetDeclineFlag,
  postApiCommunicationCheckPermissions,
  getApiCommunicationPermissionHistory,
  postApiCommunicationUpdateCommunicationPermission,
  getApiCommunicationCustomerPermissions
} from "@src/api-new/generated-api";

import { AuthReset, AuthTokenSave, AuthMemberSave } from "core/Auth";
import QueryString from "core/QueryString";
import { Info, Success } from "kit/alert";
import Storage from "core/Storage";
import DateService from "core/DateService";

import { push, replace } from "../../utils/navigation";
import STORAGE from "../../constants/STORAGE";
import CUSTOMER_STATUSES from "../../constants/CUSTOMER_STATUSES";
import { assignLoggerVariables } from "../../sagas/clientLoggerSaga";
import checkRouteAccess from "../../helpers/checkRouteAccess";

import CookieModal from "../../components/CookieModal/CookieModal.component";

import { showConfirmDialogAndWaitForAction } from "../Confirm/Confirm.saga";
import { sendCampaignParamsForConversionIfExists } from "../Shared/Shared.saga";
import routesObject from "../../routesObject";

import { appActions } from "./App.slice";



export function getQueryParameters() {
  try {
    return QueryString.parse(historyInstance.location.search);
  }
  catch (err) {
    ErrorLoggerService("error", "App.saga", err);
    return {};
  }
}

export function getRoute() {
  return matchRouteItem(historyInstance.location);
}



export function* getMember(): MemberDetailsStateType {
  const { memberDetails = {} } = yield select(state => state.app);
  return memberDetails;
}



const CUSTOMER_STATUSES_FORBBIDDEN_TO_LOGIN = [
  CUSTOMER_STATUSES.closed,
  CUSTOMER_STATUSES.none
];


export function* handleUserLogin(memberAndToken = {}, options: Object = {}, fromRegister: Boolean = false, occuredAt: String) {
  yield call(delay, 1);
  try {
    const {
      memberDetails,
      token
    }: { memberDetails: MemberDetailsStateType } = memberAndToken;
    FullStory.identify(memberDetails);
    Dengage.send(memberDetails.status, "setContactKey", memberDetails.id);
    yield fork(assignLoggerVariables, memberDetails);
    if (CUSTOMER_STATUSES_FORBBIDDEN_TO_LOGIN.includes(memberDetails.status)) {
      yield fork(Info, "Ya hay un cliente registrado con esta información. Por favor, inicia sesión de nuevo.");
      yield put(appActions.authUserLogout());
      return;
    }
    yield call(AuthTokenSave, token);
    yield call(AuthMemberSave, memberDetails);
    yield put(appActions.authUserLogin({
      memberDetails,
      token
    }));
    yield fork(sendCampaignParamsForConversionIfExists, occuredAt);
    yield fork(handleNavigationAfterLogin,
      memberDetails,
      options
    );
    yield call(initializeChatbot, memberDetails);
    if (!fromRegister) yield fork(getCommunicationPreferences, memberDetails);
  }
  catch (err) {
    window.location.replace(routesObject.home.url);
    ErrorLoggerService("APP", "App.saga", err);
  }

}



function* initializeChatbot(memberDetails) {
  const hasActiveLoan = _.isUndefined(yield select(state => state.app.hasActiveLoan)) ?
    Storage.Local.get(STORAGE.hasActiveLoan) :
    yield select(state => state.app.hasActiveLoan);
}

function* handleNavigationAfterLogin(memberDetails, {
  forceNavigate = false,
  navigateTo,
  replaceCurrentNavigationHistory = false,
  fromLogin = false
}) {
  if (navigateTo) {
    const navigate = replaceCurrentNavigationHistory ? replace : push;
    yield call(navigate, navigateTo);
  }
  else {
    const activeRoute: RouteObjectType = yield call(getRoute);
    const customerStatus = memberDetails.status;
    const {
      accessGranted,
      redirectTo
    } = checkRouteAccess(activeRoute, customerStatus);
    if (!accessGranted) {
      if (fromLogin && redirectTo === routesObject.consentAuthorization.url) {
        Storage.Session.set(STORAGE.shouldShowWelcomeMessage, true);
      }
      yield call(replace, redirectTo);
    }
  }
}

function clearSessionStorage() {
  Storage.Session.remove(STORAGE.hasSeenOverdueModal);
  Storage.Session.remove(STORAGE.hasSeenDiscountModal);
  Storage.Session.remove(STORAGE.shouldShowWelcomeMessage);
  Storage.Session.remove(STORAGE.applyIntentLog);
}



function* logoutUserAndEndSession() {
  const activeRoute = yield call(getRoute);
  const { status } = yield call(getMember);
  yield fork(assignLoggerVariables, {}, true);
  const {
    accessGranted,
    redirectTo
  } = checkRouteAccess(activeRoute, status);
  if (!accessGranted) {
    yield call(replace, redirectTo);
  }
  yield put(appActions.toggleMobileMenuVisibility(false));
  yield call(clearSessionStorage);
  yield call(AuthReset);
}


function setCookieSettings(permissions) {
  if (!permissions.fullStoryVideo) {
    FullStory.sendEvent("Consent Denied", {
      message: "Customer denied fullstory cookies"
    });
    FullStory.stopRecord();
  }
}

function* initCookie() {
  yield call(delay, 500);
  const cookiePermissions = Storage.Cookie.get(STORAGE.cookiePolicies);
  if (cookiePermissions) {
    setCookieSettings(cookiePermissions);
  }
  else {
    yield put(appActions.toggleModalState(CookieModal.MODAL, true));
  }
}

function applyCookiePermissions({ payload }) {
  Storage.Cookie.set(STORAGE.cookiePolicies, payload);
  setCookieSettings(payload);
}

function checkIfCommPermissionToggled(permissionHistory) {
  let toggledLater = false;
  if (permissionHistory.length > 1) {
    const historyAfterInitial = [ ...permissionHistory ].slice(1);
    historyAfterInitial.forEach(history => {
      if (!toggledLater) {
        const {
          allowEmail,
          allow3CxCalls,
          allowSms
        } = history;
        toggledLater = !allowEmail || !allow3CxCalls || !allowSms;
      }
    });
  }
  return toggledLater;
}

function* getCommunicationPreferences(memberDetails) {
  try {
    const lastShown = Storage.Cookie.get(STORAGE.marketingPopUpLastShown);
    const now = new Date();
    const dayDifference = lastShown ? DateService.lib(now).diff(lastShown, "days") : 0;
    if (lastShown && dayDifference < 7) return;
    const { id: customerId, status } = memberDetails;
    if (!_.includes([
      CUSTOMER_STATUSES.preRegistered,
      CUSTOMER_STATUSES.registered,
      CUSTOMER_STATUSES.systemApproved,
      CUSTOMER_STATUSES.securityChecked
    ], status)) return;
    const { data: currentPermissions, errors } = yield call(postApiCommunicationCheckPermissions, [ customerId ], {
      params: {
        customerId
      }
    });
    if (currentPermissions) {
      const {
        allowEmail,
        allow3CxCalls,
        allowSms
      } = currentPermissions[0];
      const commPrefIsZero = !allowEmail && !allow3CxCalls && !allowSms;
      if (commPrefIsZero) {
        const { data: permissionHistory } = yield call(getApiCommunicationPermissionHistory, { customerId }, {
          data: [ customerId ]
        });
        const commSwitchedOffLater = yield call(checkIfCommPermissionToggled, permissionHistory);
        if (!commSwitchedOffLater) {
          const { yes } = yield call(showConfirmDialogAndWaitForAction, {
            id: "communication-permission-welcome-back",
            title: "¡Bienvenido de nuevo!",
            content: "Haga clic en \"Sí\" para recibir comunicación personalizada sobre las últimas ofertas y descuentos de myKredit.",
            showButtonNo: true,
            buttonYes: "¡Sí!",
            buttonNo: "No"
          });
          Storage.Cookie.set(STORAGE.marketingPopUpLastShown, now);
          if (yes) {
            const { data } = yield call(postApiCommunicationUpdateCommunicationPermission, {
              allowEmail: true,
              allow3CxCalls: true,
              allowSms: true
            });
            if (data) {
              yield fork(Success, "Los cambios se han guardado.");
            }
          }
        }
      }
    }
  }
  catch (err) {
    ErrorLoggerService("APP", "App.saga", err);
  }
}

function createAffiliateSessionId() {
  if (!Storage.Session.get(STORAGE.affiliateSessionId)) {
    const generatedId = uuidv4();
    Storage.Session.set(STORAGE.affiliateSessionId, generatedId);
  }
}

export function* handleCommunicationPermissionCheck({ payload }) {
  try {
    const memberDetails = payload?.memberDetails;
    const permissionDenied = Storage.Local.get("communicationPermissionDenied");
    if (!permissionDenied) {
      const { id: customerId } = memberDetails;
      const response = yield call(getApiCommunicationCustomerPermissions, { customerId }, {
        data: [ customerId ]
      });

      if (response.data.content) {
        const { content } = response.data;
        const confirmDialogResponse = yield call(showConfirmDialogAndWaitForAction, {
          id: "communication-permission-welcome-back",
          title: "¡Bienvenido de nuevo!",
          content,
          showButtonNo: true,
          buttonYes: "¡Sí!",
          buttonNo: "No"
        });
        if (confirmDialogResponse.yes) {
          yield call(postApiCommunicationUpdateCommunicationPermission, {
            allowEmail: true,
            allow3CxCalls: true,
            allowSms: true
          });
        }
        else {
          const now = new Date();
          const expiryDate = now.setDate(now.getDate() + 7);
          Storage.Local.set("communicationPermissionDenied", true, expiryDate);
        }
      }
    }
  }
  catch (error) {
    console.error("Error occurred while handling communication permission:", error);
  }
}

export function* handleGetDeclineFlag({ payload }) {
  try {
    const memberDetails = payload?.memberDetails;
    const isSecurityChecked  = memberDetails?.status === CUSTOMER_STATUSES.securityChecked;
    if (!isSecurityChecked) return;

    const { data } = yield call(getApiCustomerGetDeclineFlag, { params: { customerId: memberDetails?.id } });
    yield put(appActions.setDeclineFlag(data?.hasDeclineFlag));
    return data?.hasDeclineFlag;
  }
  catch (error) {
    ErrorLoggerService("APP", "App.saga handleGetDeclineFlag", error);
  }
}

/* export function* handleGetChatbotPayload({ payload }) {
  try {
    const { data } = yield call(Communication.GetOpenAiRequestDtoPost, {
      data: {
        email: null,
        ticketId: null
      }
    });
    yield put(appActions.setChatbotPayload(data));
  }
  catch (error) {
    ErrorLoggerService("APP", "App.saga handleGetDeclineFlag", error);
  }
} */

export default function* sagaWatcher() {
  yield fork(initCookie);
  yield fork(createAffiliateSessionId);
  yield takeEvery(appActions.applyCookiePermissions, applyCookiePermissions);
  yield takeEvery(appActions.authUserLogout, logoutUserAndEndSession);
  yield takeEvery(appActions.authUserLogin, handleCommunicationPermissionCheck);
  yield takeEvery(appActions.authUserLogin, handleGetDeclineFlag);
  /* yield takeEvery(appActions.authUserLogin, handleGetChatbotPayload); */
}
